import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { CompanySettings_Update } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { CheckBoxFormField } from '~components/FormFields/CheckBoxFormField';
import { CompanySettings } from '~hooks/useCompanySettings/models';
import useCompanySettings from '~hooks/useCompanySettings/useCompanySettings';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

import {
  CatalogedCompanySettingKey,
  CompanySettingCatalogEntry,
  companySettingsCatalog,
} from '../companySettingsCatalog';

const settingSchema = yup.object().shape({
  checked: yup.boolean().required(),
});

type SettingDTO = yup.InferType<typeof settingSchema>;

const toOppositeWhenInverted = (value: boolean, isInverted: boolean) =>
  isInverted ? !value : value;

interface CompanySettingToggleProps {
  // shows this value instead of the company's own, e.g. the owner's value for a linked connection
  checkedOverride?: boolean;
  // Defaults to the user's own company (whose settings the store holds). Pass both to edit a
  // managed company's settings instead.
  companyId?: string;
  companySettings?: CompanySettings;
  disabled?: boolean;
  hideLabel?: boolean;
  settingKey: CatalogedCompanySettingKey;
}

export default function CompanySettingToggle({
  checkedOverride,
  companyId,
  companySettings,
  disabled = false,
  hideLabel = false,
  settingKey,
}: CompanySettingToggleProps) {
  const { userStore, toasterStore } = useStores();
  const { updateCompanySettings } = useCompanySettings();
  const [isSaving, setIsSaving] = useState(false);
  // widen past the catalog's literal entry types so the optional helper keys are readable
  const catalogEntry: CompanySettingCatalogEntry = companySettingsCatalog[settingKey];
  const isCheckboxInverted = catalogEntry.isCheckboxInverted === true;
  const targetCompanyId = companyId ?? userStore.userCompany.id;
  const initialSettings = companySettings ?? userStore.companySettings;
  const isSettingEnabled = checkedOverride ?? initialSettings.isEnabled(settingKey);

  const { control, setValue, watch } = useForm<SettingDTO>({
    resolver: yupResolver(settingSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      checked: toOppositeWhenInverted(isSettingEnabled, isCheckboxInverted),
    },
  });

  const isChecked = watch('checked');
  const previousIsChecked = useRef(isChecked);

  useEffect(() => {
    if (checkedOverride === undefined) return;

    const overriddenChecked = toOppositeWhenInverted(checkedOverride, isCheckboxInverted);
    previousIsChecked.current = overriddenChecked;
    setValue('checked', overriddenChecked);
  }, [checkedOverride, isCheckboxInverted, setValue]);

  useEffect(() => {
    // an overridden value is read-only; syncing it into the form must never save
    if (checkedOverride !== undefined) return;

    const valueDidChange = isChecked !== previousIsChecked.current;

    if (!valueDidChange) return;

    const saveSetting = async () => {
      setIsSaving(true);

      const sparseUpdate: CompanySettings_Update = {};
      sparseUpdate[settingKey] = toOppositeWhenInverted(isChecked, isCheckboxInverted);

      try {
        await updateCompanySettings(targetCompanyId, sparseUpdate);
        previousIsChecked.current = isChecked;
        toasterStore.push(
          alert(
            t('administration.configuration.configuration_saved'),
            AlertTypes.success,
          ),
        );
      } catch {
        // updateCompanySettings already toasted via handleRequestError — put the
        // checkbox back in sync with the server value it failed to change.
        setValue('checked', previousIsChecked.current);
      } finally {
        setIsSaving(false);
      }
    };

    void saveSetting();
    // toasterStore is a stable MobX singleton — deliberately omitted, since this
    // codebase's convention is that a MobX store must never sit in a dependency array.
  }, [
    checkedOverride,
    isCheckboxInverted,
    isChecked,
    setValue,
    settingKey,
    targetCompanyId,
    updateCompanySettings,
  ]);

  const checkbox = (
    <CheckBoxFormField
      control={control}
      disabled={isSaving || disabled}
      hideLabel={hideLabel}
      label={t(catalogEntry.labelKey)}
      name="checked"
      value={false}
    />
  );

  if (hideLabel) {
    return (
      <Tooltip
        placement="top"
        title={catalogEntry.descriptionKey && t(catalogEntry.descriptionKey)}
      >
        <Box sx={{ display: 'inline-flex' }}>{checkbox}</Box>
      </Tooltip>
    );
  }

  return (
    <Box>
      {checkbox}
      {catalogEntry.descriptionKey ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', pl: 4 }}>
          {t(catalogEntry.descriptionKey)}
        </Typography>
      ) : null}
    </Box>
  );
}
